/**
 * Prophylactic WebView recycle for long-running heavy video sessions on mid/low Android.
 *
 * Chromium MediaCodec sessions degrade after hours of continuous decode without a process
 * recycle — symptoms are jitter then PLAYER-1J timer-stuck, often with no MediaError until late.
 * Reactive remounts help short wedges; they do not restore a worn decoder pipeline.
 */

/** Continuous process uptime before offering a prophylactic recycle. */
export const PROPHYLACTIC_RECYCLE_UPTIME_MS = 2 * 60 * 60 * 1000

/** Minimum video creatives in the materialized queue to treat the session as video-heavy. */
export const PROPHYLACTIC_RECYCLE_MIN_VIDEO_FRAMES = 3

export interface IProphylacticPlaybackRecycleInput {
  /**
   * Monotonic ms since navigation start (`performance.now()`). Prefer this over wall clock
   * so NTP jumps cannot fire or delay recycle.
   */
  navigationUptimeMs: number
  capacityTier: 'low' | 'mid' | 'high' | null | undefined
  videoFrameCountInQueue: number
  documentVisible: boolean
}

/**
 * True when mid/low devices have been decoding a video-heavy playlist long enough that a
 * controlled WebView reload is preferable to waiting for decode storms / heartbeat watchdog.
 *
 * Note: this measures **navigation** uptime (resets on `location.reload()`), not Android
 * process lifetime — soft recycle still clears Chromium media state for the next session.
 */
export function shouldProphylacticPlaybackRecycle(
  input: IProphylacticPlaybackRecycleInput
): { recycle: boolean, reason?: string } {
  if (!input.documentVisible) {
    return { recycle: false }
  }
  if (input.capacityTier !== 'low' && input.capacityTier !== 'mid') {
    return { recycle: false }
  }
  if (input.videoFrameCountInQueue < PROPHYLACTIC_RECYCLE_MIN_VIDEO_FRAMES) {
    return { recycle: false }
  }
  const uptimeMs = input.navigationUptimeMs
  if (!(uptimeMs >= PROPHYLACTIC_RECYCLE_UPTIME_MS)) {
    return { recycle: false }
  }
  return {
    recycle: true,
    reason: `policy:prophylactic_video_recycle_${Math.floor(uptimeMs / 60000)}m_${input.capacityTier}`
  }
}
